import { useEffect, useState } from 'react';
import { localeUI } from '../lib/format';
import { Link, useParams } from 'react-router-dom';
import { Check, Flag, PenLine } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { CheckConfirmDialog, type CheckConfirm } from '../components/CheckConfirmDialog';
import { useI18n } from '../lib/i18n';
import { statusColor, daysSince } from '../lib/status';
import { useScope, ScopeTabs } from '../lib/scope';
import { useClaimDrawer, ClientNameButton } from '../components/ClaimDrawer';
import { FollowUpQueue } from './FollowUpQueue';

/** Etapas de pago que usan el sistema de follow-up (tarjetas colapsables). */
// 'supplement' entra al mismo sistema: su nav apuntaba a /queue/supplement, que NUNCA existió en
// el backend de colas genéricas (404 → la vista salía vacía); la cola real vive en followups.
const FOLLOWUP_STAGES = new Set(['acv', 'depreciation', 'mortgage', 'approved-repair', 'supplement']);

type Claim = {
  id: string;
  claim_number: string | null;
  homeowner_name: string | null;
  contractor_name: string | null;
  insurance_company: string | null;
  current_status: string | null;
  status_since: string | null;
  assigned_person: string | null;
  claim_team?: string | null;
  rcv_amount_cents: string | number | null;
  /** Quién hizo el último invoice sin emitir (Review Invoice: verlo sin abrir el claim). */
  invoice_by?: string | null;
  invoice_at?: string | null;
  /** Marcado "necesita suplemento" (supplement_review_flags): desde cuándo. Ver NeedsSupplementChip. */
  review_flag_since?: string | null;
};

/**
 * "NECESITA SUPLEMENTO" — por qué este claim está en la cola de Review Estimate.
 *
 * Reporte de Jay: en Monday, cuando aprobaban, la automatización mandaba el claim a suplementos
 * Y le ponía la etiqueta REVISION NEEDED; acá llegaban a Supplementing sin nada que mirar, así que
 * había que abrir claim por claim para encontrar cuáles necesitaban suplemento. La etiqueta existe
 * (`supplement_review_flags`) y desde hoy la cola los incluye — pero una fila que dice
 * "Supplementing" en una cola llamada Review Estimate parece un error de la cola si no dice por
 * qué está ahí. Esto es ese "por qué", con los días que lleva marcado.
 *
 * Se exporta porque la fila del claim se pinta en más de un lado (la working view del depto y el
 * board de Supplements): el chip tiene que ser UNO, o cada pantalla va a contar los días a su modo.
 */
export function NeedsSupplementChip({ since }: { since?: string | null }) {
  const { t } = useI18n();
  if (!since) return null;
  const d = daysSince(since);
  return (
    <span className="badge orange" title={t('q.needsSupplement')} style={{ whiteSpace: 'nowrap' }}>
      <Flag size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {d == null ? t('q.needsSupplement') : t('q.needsSupplementDays', { n: d })}
    </span>
  );
}

/** Colas que tienen "fecha de cheque enviado" (se escribe en Monday). */
const QUEUE_STAGE: Record<string, string> = {
  acv: 'acv',
  depreciation: 'depreciation',
  mortgage: 'mortgage',
};

/** key de la cola -> clave i18n del título (reusa las del nav). */
const TITLE_KEY: Record<string, string> = {
  acv: 'nav.acv',
  depreciation: 'nav.depreciation',
  'approved-repair': 'nav.approvedRepair',
  supplement: 'nav.supplementCheck',
  'invoice-todo': 'nav.invoiceTodo',
  review: 'nav.review',
  'waiting-clear': 'nav.waitingClear',
  pay: 'nav.payTo',
  mortgage: 'nav.mortgage',
};

/**
 * Workqueue por status de Monday: lista los claims cuyo current_status cae en la
 * cola (:key). Cada fila linkea al detalle del claim.
 */
export function QueuePage() {
  const { key = '' } = useParams();
  // Las etapas de pago usan el sistema de follow-up (tarjetas colapsables).
  if (FOLLOWUP_STAGES.has(key)) {
    return <FollowUpQueue stage={key} titleKey={TITLE_KEY[key] ?? 'nav.claims'} />;
  }
  return <GenericQueue qkey={key} />;
}

function GenericQueue({ qkey: key }: { qkey: string }) {
  const { t } = useI18n();
  const [claims, setClaims] = useState<Claim[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState('');
  const stage = QUEUE_STAGE[key];
  const [dateInput, setDateInput] = useState<Record<string, string>>({});
  const [savedFor, setSavedFor] = useState<Record<string, boolean>>({});

  /** Claim esperando confirmación: la fecha no se guarda sin decir con quién (mig 0376). */
  const [confirmando, setConfirmando] = useState<string | null>(null);
  /**
   * Claims donde el backend pidió la razón del cambio.
   *
   * Esta cola no trae el contador de ediciones de la fila (solo lista claims), así que no puede
   * saber de antemano si la fecha ya se corrigió una vez. En vez de plomear el dato hasta acá,
   * se aprende del rechazo: el backend responde `razon_requerida` y el diálogo se vuelve a abrir
   * pidiéndola. Es más robusto que adivinar, porque la regla la sigue teniendo un solo dueño.
   */
  const [pideRazon, setPideRazon] = useState<Record<string, boolean>>({});
  function saveCheckSent(claimId: string) { setConfirmando(claimId); }
  async function saveCheckSentConfirmado(claimId: string, conf: CheckConfirm) {
    const date = dateInput[claimId] || new Date().toISOString().slice(0, 10);
    try {
      await api.post(`/api/claims/${claimId}/check-sent-date`, { stage, date, ...conf });
      setConfirmando(null);
      setSavedFor((s) => ({ ...s, [claimId]: true }));
    } catch (e) {
      if (e instanceof ApiError && e.message === 'razon_requerida') {
        setPideRazon((s) => ({ ...s, [claimId]: true }));  // el diálogo sigue abierto, ahora con el campo
        return;
      }
      /* el resto se ignora, como antes */
    }
  }

  useEffect(() => {
    setLoaded(false);
    setSearch('');
    api
      .get<{ claims: Claim[] }>(`/api/claims/queue/${key}`)
      .then((r) => { setClaims(r.claims); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, [key]);

  // Busca por nombre/claim/aseguradora y también por MONTO (RCV).
  const q = search.trim().toLowerCase().replace(/[$,]/g, '');
  const buscaQ = (c: { claim_number?: string | null; homeowner_name?: string | null; contractor_name?: string | null; insurance_company?: string | null; rcv_amount_cents?: number | string | null }) => {
    if (!q) return true;
    const rcv = Number(c.rcv_amount_cents ?? 0);
    return `${c.claim_number ?? ''} ${c.homeowner_name ?? ''} ${c.contractor_name ?? ''} ${c.insurance_company ?? ''} ${(rcv / 100).toFixed(2)}`
      .toLowerCase().replace(/[$,]/g, '').includes(q);
  };
  // El buscador entra al scope: los botones Mía/All tienen que contar lo mismo que se lista.
  const sc = useScope(claims, (c) => c.assigned_person, { fallbackToAll: true, filtro: buscaQ, getTeam: (c) => c.claim_team });
  const cd = useClaimDrawer({ float: true });
  const shown = q
    ? sc.shown.filter(buscaQ)
    : sc.shown;

  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <h2 className="card-title" style={{ margin: 0 }}>
          {t(TITLE_KEY[key] ?? 'nav.claims')}{' '}
          {loaded && <span className="badge gray">{shown.length}</span>}
        </h2>
        <span style={{ flex: 1 }} />
        <input className="input" style={{ maxWidth: 260 }} placeholder={t('claims.searchPh')} value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      {loaded && <ScopeTabs {...sc} />}{cd.drawer}
      {!loaded ? (
        <div className="muted">{t('common.loading')}</div>
      ) : shown.length === 0 ? (
        <div className="muted">{t('queue.empty')}</div>
      ) : (
        <table className="tbl">
          <thead>
            <tr>
              <th>{t('claims.num')}</th>
              <th>{t('q.insurance')}</th>
              <th>{t('claims.homeowner')}</th>
              <th>{t('cd.contractorOpt')}</th>
              <th>{t('scope.assigned')}</th>
              {key === 'review' && <th>{t('q.invoiceBy')}</th>}
              <th>{t('claims.colStatus')}</th>
              <th className="num">{t('q.days')}</th>
              {stage && <th>{t('cd.checkSent')}</th>}
            </tr>
          </thead>
          <tbody>
            {shown.map((c) => {
              const d = daysSince(c.status_since);
              return (
                <tr key={c.id}>
                  <td>
                    <Link to={`/claims/${c.id}`}>
                      <strong>{c.claim_number || `#${c.id}`}</strong>
                    </Link>
                  </td>
                  <td>{c.insurance_company || '—'}</td>
                  <td><ClientNameButton claimId={c.id} name={c.homeowner_name || '—'} onOpen={cd.open} bold={false} /></td>
                  <td>{c.contractor_name || '—'}</td>
                  <td className="muted">{c.assigned_person || '—'}</td>
                  {/* Review Invoice: QUIÉN hizo el invoice, sin tener que abrir el claim. */}
                  {key === 'review' && (
                    <td>
                      {c.invoice_by ? <strong style={{ fontSize: 'var(--fs-sm)' }}><PenLine size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {c.invoice_by}</strong> : <span className="muted">—</span>}
                      {c.invoice_at && <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{new Date(c.invoice_at).toLocaleDateString(localeUI())}</div>}
                    </td>
                  )}
                  <td style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span className={`badge ${statusColor(c.current_status)}`}>{c.current_status}</span>
                    {/* El claim puede estar en la cola por la BANDERA y no por su estatus: se dice. */}
                    <NeedsSupplementChip since={c.review_flag_since} />
                  </td>
                  <td className="num">{d == null ? '—' : `${d}d`}</td>
                  {stage && (
                    <td style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                      {savedFor[c.id] ? (
                        <span style={{ color: 'var(--green)', fontSize: 'var(--fs-sm)' }}><Check size={14} strokeWidth={2} style={{ verticalAlign: 'text-bottom' }} /></span>
                      ) : (
                        <>
                          <input className="input sm" type="date" style={{ width: 140 }}
                            value={dateInput[c.id] ?? ''} onChange={(e) => setDateInput((s) => ({ ...s, [c.id]: e.target.value }))} />
                          <button className="btn sm" onClick={() => saveCheckSent(c.id)}>{t('cd.checkSent')}</button>
                          {confirmando === c.id && (
                            <div style={{ flexBasis: '100%' }}>
                              <CheckConfirmDialog needsReason={pideRazon[c.id] ?? false} onCancel={() => setConfirmando(null)} onConfirm={(cf) => saveCheckSentConfirmado(c.id, cf)} />
                            </div>
                          )}
                        </>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
